import type { AgencyDesignChoices } from '~/api/data/designs/queries';
import { Action, Resource, useGetAgencyDesignChoicesQuery } from '~/api/graphql';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { usePermissions } from '~/components/permissions';

const USE_DESIGNS = [
  { resource: Resource.HtmlDocumentTemplate, action: Action.List },
  { resource: Resource.HtmlDocumentTemplate, action: Action.Read }
];

/**
 * The designs a document can switch to. Null while designs are off, so no document asks for a
 * list that would be empty, and without the Styles permission, which designs share, since the
 * server answers such a caller with an error rather than an empty list. Read from the cache
 * first, so a document opened again shows its Design menu at once; `refresh` asks again, for a
 * menu opened after designs changed elsewhere. `loading` is true while the first answer is
 * awaited with none cached, when null choices do not yet mean the agency has none.
 */
export const useDocumentDesigns = (): {
  choices: AgencyDesignChoices | null;
  loading: boolean;
  refresh: () => void;
} => {
  const designsOn = useFlag(FEATURE_FLAGS.AGENCY_BRAND_DESIGN);
  const canList = usePermissions().hasAllPermissions(USE_DESIGNS);
  const skip = !designsOn || !canList;
  const { data, loading, refetch } = useGetAgencyDesignChoicesQuery({
    skip,
    fetchPolicy: 'cache-and-network'
  });
  return {
    choices: skip ? null : (data?.agency ?? null),
    loading: !skip && loading && !data,
    refresh: () => {
      if (!skip) void refetch();
    }
  };
};
